import BulkUpdateModal from '../../components/BulkUpdateModal';
import IngredientVendorsModal from '../../components/IngredientVendorsModal';
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
// Ingredients now loaded from Supabase
import { getRequisitions, updateRequisition, supabase, getIngredientCustomizations, saveIngredientCustomization, deleteIngredientDB, setInstructorVisibility, bulkSetInstructorVisibility, getIngredients, saveIngredient, deleteIngredient } from "../../lib/supabase";
import { useSettings } from "../../hooks/useSettings";
import { calculateUnitPrice } from "../../utils/packSizeParser";

export default function IngredientsPage() {
  const [searchParams] = useSearchParams();
  const { categoryStructure, categories, vendors, getBrandsForSubcategory, refreshSettings } = useSettings();
  const [ingredients, setIngredients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [filterProgram, setFilterProgram] = useState('');
  const [filterMissing, setFilterMissing] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [showCustomUnit, setShowCustomUnit] = useState(false);
  const [showCustomBrand, setShowCustomBrand] = useState(false);
  const [customUnitValue, setCustomUnitValue] = useState('');
  const [customBrandValue, setCustomBrandValue] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVendorsModal, setShowVendorsModal] = useState(false);
  const [vendorModalIngredient, setVendorModalIngredient] = useState(null);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState([]);
  const [importMatches, setImportMatches] = useState({});
  const [openMenuId, setOpenMenuId] = useState(null);
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuId]);

  const [viewMode, setViewMode] = useState('grouped');
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState(new Set());
  const [instructorHidden, setInstructorHidden] = useState(new Set());
  const [matchingItem, setMatchingItem] = useState(null);
  const [matchSearch, setMatchSearch] = useState('');
  const [columnOrder, setColumnOrder] = useState(['name', 'brand', 'category', 'subcategory', 'unit', 'vendor', 'vendorCode', 'packSize', 'casePrice', 'unitPrice', 'programs', 'instructor', 'lastUpdated']);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const fileInputRef = useRef(null);
  
  const [newIngredient, setNewIngredient] = useState({
    name: '', category: 'Produce', subcategory: 'Other', unit: 'lb',
    vendor: 'Sysco', vendorCode: '', packSize: '', casePrice: 0, unitPrice: 0, servingsPerCase: 0, programs: []
  });

  const units = ['lb', 'oz', 'g', 'kg', 'ea', 'doz', 'bunch', 'qt', 'gal', 'pt', 'cup', 'can', 'bottle', 'jar', 'bag', 'box', 'case', 'c', 'L', 'mL', 'rack', 'slab', 'sheet', 'loaf', 'head', 'pkg', '12oz pkg', '16oz pkg', '8oz pkg', '1lb pkg', '5lb bag', '10lb bag', '25lb bag', '50lb bag', '#10 can', 'pint', 'quart', 'half gal', 'liter', '750ml'];

  const getUnitsForCategory = (category, subcategory) => {
    const cat = (category || '').toLowerCase();
    const sub = (subcategory || '').toLowerCase();
    if (sub.includes('egg')) return ['ea', 'doz'];
    if (sub.includes('oil') || sub.includes('fat') || sub.includes('vinegar') || sub.includes('sauce') || sub.includes('milk') || sub.includes('cream') || sub.includes('juice') || sub.includes('stock') || cat.includes('beverage')) return ['oz', 'cup', 'pt', 'qt', 'gal', 'L', 'mL'];
    if (sub.includes('flour') || sub.includes('starch') || sub.includes('grain') || sub.includes('sugar') || sub.includes('sweet') || sub.includes('rice') || sub.includes('pasta') || sub.includes('spice') || sub.includes('leaven')) return ['oz', 'lb', 'g', 'kg', 'cup'];
    if (cat.includes('produce')) return ['ea', 'bunch', 'head', 'lb', 'oz'];
    if (cat.includes('meat') || cat.includes('seafood')) return ['lb', 'oz', 'ea', 'rack', 'slab'];
    if (cat.includes('dairy')) return ['lb', 'oz', 'ea', 'cup', 'gal', 'qt'];
    if (cat.includes('bakery')) return ['ea', 'loaf', 'doz', 'lb', 'oz'];
    return ['ea', 'lb', 'oz', 'cup', 'pt', 'qt', 'gal', 'doz'];
  };

  // Load ingredients from Supabase
  useEffect(() => {
    loadIngredients();
    const editName = searchParams.get('edit');
    if (editName) setSearchTerm(editName);
  }, [searchParams]);

  const loadIngredients = async () => {
    try {
      // Load core ingredients
      const { data: ingredientsData, error: ingError } = await supabase
        .from('ingredients')
        .select('*')
        .order('name');
      if (ingError) throw ingError;

      // Load vendor data (prefer is_preferred, then Sysco)
      const { data: vendorsData, error: vendorError } = await supabase
        .from('ingredient_vendors')
        .select('*')
        .order('is_preferred', { ascending: false });
      if (vendorError) throw vendorError;

      // Group vendors by ingredient name, pick preferred
      const vendorMap = {};
      (vendorsData || []).forEach(v => {
        if (!vendorMap[v.ingredient_name]) {
          vendorMap[v.ingredient_name] = v;
        } else if (v.is_preferred && !vendorMap[v.ingredient_name].is_preferred) {
          vendorMap[v.ingredient_name] = v;
        }
      });

      // Load inventory data
      const { data: inventoryData, error: invError } = await supabase
        .from('inventory_current')
        .select('ingredient_name, quantity, last_counted');
      if (invError) throw invError;

      const inventoryMap = {};
      (inventoryData || []).forEach(i => {
        inventoryMap[i.ingredient_name] = {
          onHand: i.quantity,
          lastCounted: i.last_counted
        };
      });

      // Merge all data
      const mapped = ingredientsData.map(row => {
        const vendor = vendorMap[row.name] || {};
        const inv = inventoryMap[row.name] || {};
        return {
          id: row.id,
          name: row.name,
          category: row.category,
          subcategory: row.subcategory,
          unit: row.unit,
          vendor: vendor.vendor || row.vendor || '-',
          itemNumber: vendor.item_number || row.vendor_code || '',
          vendorDescription: vendor.vendor_description || '',
          packSize: vendor.pack_size || row.pack_size || '',
          casePrice: vendor.case_price || row.case_price || 0,
          unitPrice: row.unit_price || 0,
          brand: row.brand,
          programs: row.programs || ['Baking & Pastry Arts', 'Culinary Arts', 'Foodservice'],
          storage: row.storage,
          onHand: inv.onHand,
          lastCounted: inv.lastCounted,
          lastUpdated: row.updated_at,
          hiddenFromInstructor: row.hidden_from_instructor
        };
      });
      setIngredients(mapped);
      const hidden = new Set(mapped.filter(i => i.hiddenFromInstructor).map(i => i.id));
      setInstructorHidden(hidden);
    } catch (err) {
      console.error('Error loading ingredients:', err);
    }
  };

  // Format last counted date
  const formatLastCounted = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'today';
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Calculate unit price from pack size
  const calculateUnitPriceFromPack = (item) => {
    if (!item.packSize || !item.casePrice) return null;
    const pack = item.packSize.toString().trim();
    const casePrice = parseFloat(item.casePrice) || 0;
    const unit = (item.unit || '').toLowerCase();
    
    const toOz = (num, m) => { m = (m || '').toUpperCase(); if (m === 'OZ') return num; if (m === 'LB') return num * 16; if (m === 'G') return num / 28.35; if (m === 'KG') return num * 35.27; return null; };
    const toFlOz = (num, m) => { m = (m || '').toUpperCase(); if (m === 'OZ' || m === 'FLOZ') return num; if (m === 'GAL') return num * 128; if (m === 'QT') return num * 32; if (m === 'PT') return num * 16; if (m === 'CUP' || m === 'C') return num * 8; if (m === 'L') return num * 33.814; if (m === 'ML') return num / 29.574; return null; };
    const toEach = (num, m) => { m = (m || '').toUpperCase(); if (m === 'DZ' || m === 'DOZ') return num * 12; return num; };
    
    const getUnitInfo = (u) => {
      if (/^ea$/i.test(u)) return { size: 1, type: 'count' };
      if (/^doz$/i.test(u)) return { size: 12, type: 'count' };
      if (/^oz$/i.test(u)) return { size: 1, measure: 'OZ', type: 'weight' };
      if (/^lb$/i.test(u)) return { size: 16, measure: 'OZ', type: 'weight' };
      if (/^gal$/i.test(u)) return { size: 128, measure: 'FLOZ', type: 'volume' };
      if (/^qt$/i.test(u)) return { size: 32, measure: 'FLOZ', type: 'volume' };
      if (/^pt$/i.test(u)) return { size: 16, measure: 'FLOZ', type: 'volume' };
      if (/^cup$/i.test(u) || /^c$/i.test(u)) return { size: 8, measure: 'FLOZ', type: 'volume' };
      const m = u.match(/(\d+\.?\d*)\s*(oz|lb|g|kg)/i);
      if (m) return { size: toOz(parseFloat(m[1]), m[2]), measure: 'OZ', type: 'weight' };
      return null;
    };
    
    let packCount = 1, packSize = null, packMeasure = null;
    const slashMatch = pack.match(/^(\d+)\s*\/\s*(\d+\.?\d*)\s*(oz|lb|g|kg|gal|qt|pt|l|ml|dz|doz|ea|ct)?$/i);
    if (slashMatch) { packCount = parseFloat(slashMatch[1]); packSize = parseFloat(slashMatch[2]); packMeasure = (slashMatch[3] || 'EA').toUpperCase(); }
    else { const singleMatch = pack.match(/^(\d+\.?\d*)\s*(oz|lb|g|kg|gal|qt|pt|l|ml|dz|doz)$/i); if (singleMatch) { packSize = parseFloat(singleMatch[1]); packMeasure = singleMatch[2].toUpperCase(); } else { const countMatch = pack.match(/^(\d+)/); if (countMatch) packCount = parseFloat(countMatch[1]); } }
    
    const unitInfo = getUnitInfo(unit);
    if (!unitInfo) { if (packMeasure) return null; if (packCount > 0 && !packSize) return casePrice / packCount; return null; }
    
    const countMeasures = ['EA', 'DZ', 'DOZ', 'CT'];
    const volMeasures = ['GAL', 'QT', 'PT', 'L', 'ML', 'OZ'];
    const wtMeasures = ['OZ', 'LB', 'G', 'KG'];
    
    if (unitInfo.type === 'count') { if (packMeasure && countMeasures.includes(packMeasure)) { const totalEach = packCount * toEach(packSize || 1, packMeasure); return casePrice / (totalEach / unitInfo.size); } if (!packMeasure) return casePrice / packCount; return null; }
    if ((unitInfo.type === 'volume' || (unit === 'oz' && volMeasures.includes(packMeasure))) && packMeasure && volMeasures.includes(packMeasure)) { const totalFlOz = toFlOz(packCount * (packSize || 1), packMeasure); const unitFlOz = unit === 'oz' ? 1 : unitInfo.size; if (totalFlOz) return casePrice / (totalFlOz / unitFlOz); }
    if (unitInfo.type === 'weight' && packMeasure && wtMeasures.includes(packMeasure)) { const totalOz = toOz(packCount * packSize, packMeasure); if (totalOz) return casePrice / (totalOz / unitInfo.size); }
    if (unitInfo.type === 'weight' && /pkg|pack|bag|box|can|jar/i.test(unit) && packMeasure && wtMeasures.includes(packMeasure)) { const totalOz = toOz(packCount * packSize, packMeasure); if (totalOz) return casePrice / (totalOz / unitInfo.size); }
    
    return null;
  };

  const getCalculatedPrices = (item) => {
    const casePrice = item.casePrice || item.syscoPrice || 0;
    const unitPrice = calculateUnitPriceFromPack({ ...item, casePrice }) || item.unitPrice || 0;
    return { casePrice, unitPrice };
  };

  // Column definitions
  const columnDefs = {
    name: { label: 'Name', align: 'left', sortable: true, render: (ing) => <span className="font-medium">{ing.name}</span> },
    brand: { label: 'Brand', align: 'left', sortable: true, render: (ing) => ing.brand || '-' },
    category: { label: 'Category', align: 'left', sortable: true, render: (ing) => ing.category },
    subcategory: { label: 'Subcategory', align: 'left', sortable: true, render: (ing) => ing.subcategory || '-' },
    unit: { label: 'Unit', align: 'center', sortable: true, render: (ing) => ing.unit },
    vendor: { label: 'Vendor', align: 'left', sortable: true, render: (ing) => ing.vendor || 'Sysco' },
    vendorCode: { label: 'UPC', align: 'left', sortable: true, render: (ing) => <span className="font-mono text-xs">{ing.vendorCode || '-'}</span> },
    packSize: { label: 'Pack', align: 'left', sortable: true, render: (ing) => ing.packSize || '-' },
    casePrice: { label: 'Case $', align: 'right', sortable: true, render: (ing) => `$${(ing.casePrice || 0).toFixed(2)}` },
    unitPrice: { label: '$/Unit', align: 'right', sortable: true, render: (ing) => <span className="text-green-700 font-medium">${(calculateUnitPriceFromPack(ing) || ing.unitPrice || 0).toFixed(4)}</span> },
    programs: { label: 'Prog', align: 'center', sortable: false, render: (ing) => <span className="text-xs">{(ing.programs || []).map(p => p === 'Baking & Pastry Arts' ? 'B' : p === 'Culinary Arts' ? 'C' : 'F').join('')}</span> },
    instructor: { label: 'Vis', align: 'center', sortable: false, render: (ing) => <button onClick={() => toggleInstructorVisibility(ing.id)} className={`text-lg ${instructorHidden.has(ing.id) ? 'text-red-500' : 'text-green-600'}`}>{instructorHidden.has(ing.id) ? '✕' : '✓'}</button> },
    lastUpdated: { label: 'Updated', align: 'left', sortable: true, render: (ing) => ing.lastUpdated ? new Date(ing.lastUpdated).toLocaleDateString() : '-' }
  };

  // Get unique values for filters
  const allCategories = [...new Set(ingredients.map(i => i.category))].filter(Boolean).sort();
  const allSubcategories = [...new Set(ingredients.filter(i => !filterCategory || i.category === filterCategory).map(i => i.subcategory))].filter(Boolean).sort();
  const allVendors = [...new Set(ingredients.map(i => i.vendor))].filter(Boolean).sort();

  // Toggle instructor visibility
  const toggleInstructorVisibility = async (id) => {
    const newHidden = !instructorHidden.has(id);
    setInstructorHidden(prev => {
      const next = new Set(prev);
      if (newHidden) next.add(id);
      else next.delete(id);
      return next;
    });
    await setInstructorVisibility(id, newHidden);
  };

  const hideSelectedFromInstructor = async () => {
    const ids = Array.from(selectedItems);
    setInstructorHidden(prev => new Set([...prev, ...ids]));
    await bulkSetInstructorVisibility(ids, true);
  };

  const showSelectedToInstructor = async () => {
    const ids = Array.from(selectedItems);
    setInstructorHidden(prev => {
      const next = new Set(prev);
      ids.forEach(id => next.delete(id));
      return next;
    });
    await bulkSetInstructorVisibility(ids, false);
  };

  // Toggle program for ingredient
  const toggleProgram = async (id, program) => {
    const ing = ingredients.find(i => i.id === id);
    if (!ing) return;
    const current = ing.programs || [];
    const updated = current.includes(program)
      ? current.filter(p => p !== program)
      : [...current, program];
    await saveIngredientEdit(id, { programs: updated });
    setIngredients(prev => prev.map(i => i.id === id ? { ...i, programs: updated } : i));
  };

  // Save ingredient edit to Supabase
  const saveIngredientEdit = async (id, updates) => {
    const timestamp = new Date().toISOString();
    const validFields = ['name', 'category', 'subcategory', 'unit', 'vendor', 'brand', 'programs', 'storage'];
    const fieldMap = {
      vendorCode: 'vendor_code',
      packSize: 'pack_size',
      casePrice: 'case_price',
      unitPrice: 'unit_price',
      hiddenFromInstructor: 'hidden_from_instructor'
    };
    const dbUpdates = { updated_at: timestamp };
    for (const [key, value] of Object.entries(updates)) {
      if (validFields.includes(key)) {
        dbUpdates[key] = value;
      } else if (fieldMap[key]) {
        dbUpdates[fieldMap[key]] = value;
      }
    }
    const { error } = await supabase.from('ingredients').update(dbUpdates).eq('id', id);
    if (error) {
      console.error('Save error:', error);
      alert('Save failed: ' + error.message);
    }
    return timestamp;
  };

  // Inline edit handler
  const handleInlineEdit = async (id, field, value) => {
    await saveIngredientEdit(id, { [field]: value });
    setIngredients(prev => prev.map(i => i.id === id ? { ...i, [field]: value } : i));
  };

  // Edit modal handlers
  const handleEdit = (ingredient) => {
    setEditingId(ingredient.id);
    setEditForm({
      ...ingredient,
      vendor: ingredient.vendor || 'Sysco',
      vendorCode: ingredient.vendorCode || '',
      packSize: ingredient.packSize || '',
      casePrice: ingredient.casePrice || 0,
      unitPrice: ingredient.unitPrice || 0,
      subcategory: ingredient.subcategory || 'Other',
      programs: ingredient.programs || ['Baking & Pastry Arts', 'Culinary Arts', 'Foodservice']
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    const timestamp = await saveIngredientEdit(editingId, editForm);
    setIngredients(prev => prev.map(i => i.id === editingId ? { ...i, ...editForm, lastUpdated: timestamp } : i));
    setEditingId(null);
    setEditForm({});
    setShowEditModal(false);
  };

  const handleCancelEdit = () => { setEditingId(null); setEditForm({}); setShowEditModal(false); };

  // Add new ingredient
  const handleAddIngredient = async () => {
    if (!newIngredient.name.trim()) { alert('Please enter an ingredient name'); return; }
    const id = 'CUSTOM-' + Date.now();
    const timestamp = new Date().toISOString();
    const ingredient = { ...newIngredient, id, lastUpdated: timestamp };
    await saveIngredientCustomization(id, ingredient, true);
    setIngredients(prev => [...prev, ingredient]);
    setNewIngredient({ name: '', category: 'Produce', subcategory: 'Other', unit: 'lb', vendor: 'Sysco', vendorCode: '', packSize: '', casePrice: 0, unitPrice: 0, servingsPerCase: 0, programs: [] });
    setShowAddForm(false);
  };

  // Delete ingredient
  const handleDeleteIngredient = async (id) => {
    const ing = ingredients.find(i => i.id === id);
    if (!window.confirm("Delete " + (ing?.name || "this ingredient") + "?")) return;
    const { error } = await supabase.from('ingredients').delete().eq('id', id);
    if (error) { console.error('Delete failed:', error); alert('Delete failed'); return; }
    setIngredients(prev => prev.filter(i => i.id !== id));
    setOpenMenuId(null);
  };

  // Duplicate ingredient
  const handleDuplicateIngredient = async (ing) => {
    const id = "CUSTOM-" + Date.now();
    const duplicate = { ...ing, id, name: ing.name + " (Copy)", lastUpdated: new Date().toISOString() };
    await saveIngredientCustomization(id, duplicate, true);
    setIngredients(prev => [...prev, duplicate]);
    setOpenMenuId(null);
  };

  // Export to CSV
  const exportIngredients = () => {
    let csv = 'Name,Brand,Category,Subcategory,Unit,Vendor,Code,Pack Size,Case Price,Unit Price\n';
    filteredIngredients.forEach(ing => {
      csv += `"${ing.name}","${ing.brand || ''}","${ing.category}","${ing.subcategory || ''}","${ing.unit}","${ing.vendor || 'Sysco'}","${ing.vendorCode || ''}","${ing.packSize || ''}",${(ing.casePrice || 0).toFixed(2)},${(ing.unitPrice || 0).toFixed(4)}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ingredients.csv';
    a.click();
  };

  // Selection handlers
  const toggleSelected = (id) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedItems(new Set(filteredIngredients.map(i => i.id)));
  const selectNone = () => setSelectedItems(new Set());

  // Bulk update handlers
  const bulkUpdateCategory = async (category) => {
    if (!category) return;
    const ids = Array.from(selectedItems);
    for (const id of ids) {
      await saveIngredientEdit(id, { category });
    }
    setIngredients(prev => prev.map(i => selectedItems.has(i.id) ? { ...i, category } : i));
  };

  const bulkUpdateVendor = async (vendor) => {
    if (!vendor) return;
    const ids = Array.from(selectedItems);
    for (const id of ids) {
      await saveIngredientEdit(id, { vendor });
    }
    setIngredients(prev => prev.map(i => selectedItems.has(i.id) ? { ...i, vendor } : i));
  };

  // Sort handler
  const handleSort = (column) => {
    if (!columnDefs[column]?.sortable) return;
    if (sortColumn === column) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(column); setSortDirection('asc'); }
  };

  const getSortValue = (ing, col) => {
    switch (col) {
      case 'name': return ing.name || '';
      case 'brand': return ing.brand || '';
      case 'category': return ing.category || '';
      case 'subcategory': return ing.subcategory || '';
      case 'unit': return ing.unit || '';
      case 'vendor': return ing.vendor || '';
      case 'vendorCode': return ing.vendorCode || '';
      case 'packSize': return ing.packSize || '';
      case 'casePrice': return ing.casePrice || 0;
      case 'unitPrice': return ing.unitPrice || 0;
      case 'lastUpdated': return ing.lastUpdated || '';
      default: return '';
    }
  };

  // Column drag handlers
  const handleColumnDragStart = (e, col) => { setDraggedColumn(col); e.dataTransfer.effectAllowed = 'move'; };
  const handleColumnDragOver = (e, col) => { e.preventDefault(); setDragOverColumn(col); };
  const handleColumnDrop = (e, col) => {
    e.preventDefault();
    if (!draggedColumn || draggedColumn === col) return;
    const newOrder = [...columnOrder];
    const fromIdx = newOrder.indexOf(draggedColumn);
    const toIdx = newOrder.indexOf(col);
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggedColumn);
    setColumnOrder(newOrder);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };
  const handleColumnDragEnd = () => { setDraggedColumn(null); setDragOverColumn(null); };

  // Filter ingredients
  const filteredIngredients = ingredients.filter(ing => {
    const term = searchTerm.toLowerCase();
    const termNorm = term.replace(/s$/, '');
    const nameNorm = ing.name.toLowerCase().replace(/s$/, '');
    const matchesSearch = !term || nameNorm.includes(termNorm) || ing.name.toLowerCase().includes(term) || (ing.brand || '').toLowerCase().includes(term) || (ing.vendorCode || '').toLowerCase().includes(term);
    const matchesCategory = !filterCategory || ing.category === filterCategory;
    const matchesVendor = !filterVendor || ing.vendor === filterVendor;
    const matchesSubcategory = !filterSubcategory || ing.subcategory === filterSubcategory;
    const matchesProgram = !filterProgram || (ing.programs || []).includes(filterProgram);
    const casePrice = ing.casePrice || 0;
    const vendorCode = ing.vendorCode || '';
    const matchesMissing = !filterMissing || 
      (filterMissing === 'price' && casePrice === 0) ||
      (filterMissing === 'code' && !vendorCode) ||
      (filterMissing === 'both' && (casePrice === 0 || !vendorCode));
    return matchesSearch && matchesCategory && matchesVendor && matchesSubcategory && matchesProgram && matchesMissing;
  }).sort((a, b) => {
    const aVal = getSortValue(a, sortColumn);
    const bVal = getSortValue(b, sortColumn);
    const cmp = typeof aVal === 'number' ? aVal - bVal : String(aVal).localeCompare(String(bVal));
    return sortDirection === 'asc' ? cmp : -cmp;
  });

  // Group ingredients by category/subcategory
  const groupedIngredients = useMemo(() => {
    const groups = {};
    filteredIngredients.forEach(item => {
      const cat = item.category || 'Other';
      const subcat = item.subcategory || 'General';
      if (!groups[cat]) groups[cat] = {};
      if (!groups[cat][subcat]) groups[cat][subcat] = [];
      groups[cat][subcat].push(item);
    });
    const sortedGroups = {};
    Object.keys(groups).sort().forEach(cat => {
      sortedGroups[cat] = {};
      Object.keys(groups[cat]).sort().forEach(subcat => {
        sortedGroups[cat][subcat] = groups[cat][subcat];
      });
    });
    return sortedGroups;
  }, [filteredIngredients]);

  const visibleColumns = showAllColumns
    ? columnOrder
    : columnOrder.filter(c => !['vendorCode', 'lastUpdated'].includes(c));

  const allInViewSelected = filteredIngredients.length > 0 && filteredIngredients.every(i => selectedItems.has(i.id));

  // Ingredient Card component for card view
  const IngredientCard = ({ ing }) => {
    const isSelected = selectedItems.has(ing.id);
    const isHidden = instructorHidden.has(ing.id);
    return (
      <div className={`border rounded-lg shadow-sm hover:shadow-md transition-shadow ${isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'}`}>
        <div className="p-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <button onClick={() => toggleSelected(ing.id)} className={`w-5 h-5 rounded border flex items-center justify-center text-xs flex-shrink-0 ${isSelected ? 'bg-green-500 text-white border-green-600' : 'bg-white border-gray-400'}`}>{isSelected ? '✓' : ''}</button>
              <div>
                <h4 className="font-medium text-sm">{ing.name}</h4>
                {ing.brand && <p className="text-xs text-blue-600">{ing.brand}</p>}
              </div>
            </div>
            <button onClick={() => toggleInstructorVisibility(ing.id)} className={`text-sm px-1 ${isHidden ? 'text-red-500' : 'text-green-600'}`}>{isHidden ? '✕' : '✓'}</button>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            <p>{ing.category} › {ing.subcategory || 'General'}</p>
            <p>{ing.vendor || 'Sysco'} {ing.vendorCode && `• ${ing.vendorCode}`}</p>
          </div>
          <div className="mt-2 flex justify-between items-center">
            <span className="text-xs">{ing.packSize || '-'}</span>
            <div className="text-right">
              <p className="text-sm font-medium">${(ing.casePrice || 0).toFixed(2)}</p>
              <p className="text-xs text-green-700">${(calculateUnitPriceFromPack(ing) || 0).toFixed(4)}/{ing.unit}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Ingredients</h2>
          <p className="text-sm text-gray-500">{filteredIngredients.length} of {ingredients.length} items</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowBulkUpdate(true)} className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600">⚠️ Needs Update</button>
          <button onClick={() => setShowAddForm(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">+ Add</button>
          <button onClick={exportIngredients} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">↓ Export</button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border rounded-lg p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-64">
            <input
              type="text"
              placeholder="Search ingredients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>
          <select value={filterCategory} onChange={(e) => { setFilterCategory(e.target.value); setFilterSubcategory(''); }} className="px-3 py-2 border rounded-lg">
            <option value="">All Categories</option>
            {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={filterSubcategory} onChange={(e) => setFilterSubcategory(e.target.value)} className="px-3 py-2 border rounded-lg">
            <option value="">All Subcategories</option>
            {allSubcategories.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)} className="px-3 py-2 border rounded-lg">
            <option value="">All Vendors</option>
            {allVendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <select value={filterProgram} onChange={(e) => setFilterProgram(e.target.value)} className="px-3 py-2 border rounded-lg">
            <option value="">All Programs</option>
            <option value="Baking & Pastry Arts">Baking</option>
            <option value="Culinary Arts">Culinary</option>
            <option value="Foodservice">Foodservice</option>
          </select>
          <select value={filterMissing} onChange={(e) => setFilterMissing(e.target.value)} className="px-3 py-2 border rounded-lg bg-yellow-50">
            <option value="">All Items</option>
            <option value="price">Missing Price</option>
            <option value="code">Missing Code</option>
            <option value="both">Missing Any</option>
          </select>
        </div>
        <div className="flex justify-between items-center mt-3 pt-3 border-t">
          <div className="flex items-center gap-2">
            <button onClick={() => setEditMode(!editMode)} className={`px-3 py-1.5 text-sm rounded ${editMode ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {editMode ? '✓ Editing...' : '✎ Edit Mode'}
            </button>
            {editMode && <span className="text-sm text-orange-600">Click any field to edit. Changes save on blur.</span>}
            <span className="text-sm text-gray-500 ml-4">{filteredIngredients.length} items</span>
          </div>
          <div className="flex items-center">
            <button onClick={() => setViewMode('grouped')} className={`px-3 py-1.5 text-sm rounded-l border ${viewMode === 'grouped' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>Grouped</button>
            <button onClick={() => setViewMode('table')} className={`px-3 py-1.5 text-sm rounded-r border-t border-b border-r ${viewMode === 'table' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>Table</button>
            {viewMode === 'table' && <label className="flex items-center gap-1 text-xs ml-2"><input type="checkbox" checked={showAllColumns} onChange={(e) => setShowAllColumns(e.target.checked)} /> All Cols</label>}
          </div>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedItems.size > 0 && (
        <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-300 flex flex-wrap gap-3 items-center">
          <span className="font-bold text-green-800">✓ {selectedItems.size}</span>
          <select onChange={(e) => { bulkUpdateCategory(e.target.value); e.target.value = ''; }} className="px-2 py-1 border rounded text-xs">
            <option value="">Category...</option>
            {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select onChange={(e) => { bulkUpdateVendor(e.target.value); e.target.value = ''; }} className="px-2 py-1 border rounded text-xs">
            <option value="">Vendor...</option>
            {allVendors.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
          <button onClick={hideSelectedFromInstructor} className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Hide</button>
          <button onClick={showSelectedToInstructor} className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Show</button>
          <button onClick={selectNone} className="px-2 py-1 bg-gray-200 rounded text-xs">Clear</button>
        </div>
      )}

      {/* Grouped View */}
      {viewMode === 'grouped' && (
        <div className="space-y-6">
          {Object.entries(groupedIngredients).map(([category, subcategories]) => (
            <div key={category} className="border rounded-lg overflow-hidden">
              <h4 className="font-bold text-blue-800 bg-blue-100 px-4 py-3 text-lg">{category}</h4>
              <table className="w-full">
                <thead className="text-xs text-gray-500 bg-gray-50">
                  <tr>
                    <th className="w-6 p-1"></th>
                    <th className="w-8 p-2"></th>
                    <th className="text-left px-3 py-2">Item</th>
                    <th className="text-left px-3 py-2 w-20">Vendor</th>
                    <th className="text-left px-3 py-2 w-24">Item #</th>
                    <th className="text-left px-3 py-2 w-20">Pack</th>
                    <th className="text-right px-3 py-2 w-20">Case $</th>
                    <th className="text-center px-3 py-2 w-16">On Hand</th>
                    <th className="text-center px-3 py-2 w-20">Updated</th>
                    <th className="w-12 p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(subcategories).map(([subcategory, items]) => (
                    <React.Fragment key={subcategory}>
                      <tr className="bg-blue-50">
                        <td colSpan={10} className="px-4 py-2 font-semibold text-blue-600 border-l-4 border-blue-400">
                          {subcategory} ({items.length})
                        </td>
                      </tr>
                      {items.map(item => {
                        const isSelected = selectedItems.has(item.id);
                        const casePrice = item.casePrice || 0;
                        return (
                          <tr key={item.id} className={`border-t border-gray-100 hover:bg-gray-50 ${isSelected ? 'bg-green-50' : ''} `}>
                            <td className="p-1 text-center cursor-grab text-gray-400 hover:text-gray-600">⋮⋮</td>
                            <td className="p-2 text-center">
                              <button onClick={() => toggleSelected(item.id)} className={`w-5 h-5 rounded border flex items-center justify-center text-xs mx-auto ${isSelected ? 'bg-green-500 text-white border-green-600' : 'bg-white border-gray-400'}`}>
                                {isSelected ? '✓' : ''}
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-semibold text-gray-900">{item.name}</div>
                              <div className="text-xs text-gray-500">{item.unit} {item.vendorDescription ? `• ${item.vendorDescription}` : ''}</div>
                            </td>
                            <td className="px-3 py-2 text-gray-600 text-sm">{item.vendor || '-'}</td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-500">{item.itemNumber || '-'}</td>
                            <td className="px-3 py-2 text-gray-600 text-xs">{item.packSize || '-'}</td>
                            <td className="px-3 py-2 text-right">${casePrice.toFixed(2)}</td>
                            <td className="px-3 py-2 text-center">
                              {item.onHand !== null && item.onHand !== undefined ? (
                                <span className={`font-medium ${item.onHand > 0 ? 'text-green-600' : 'text-gray-400'}`}>{item.onHand}</span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center text-xs text-gray-400">
                              {formatLastCounted(item.lastCounted)}
                            </td>
                            <td className="p-2 text-center relative">
                              <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === item.id ? null : item.id); }} className="px-2 py-1 text-gray-500 hover:bg-gray-100 rounded">⋮</button>
                              {openMenuId === item.id && (
                                <div className="absolute right-0 bottom-full mb-1 bg-white border rounded-lg shadow-lg z-50 py-1 min-w-32">
                                  <button onClick={() => { handleEdit(item); setOpenMenuId(null); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100">✏️ Edit</button>
                                  <button onClick={() => { setVendorModalIngredient(item); setShowVendorsModal(true); setOpenMenuId(null); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100">🏪 Vendors</button>
                                  <button onClick={() => { handleDuplicateIngredient(item); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100">⧉ Copy</button>
                                  <button onClick={() => { handleDeleteIngredient(item.id); }} className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50">🗑 Delete</button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border w-8">
                  <button onClick={() => allInViewSelected ? selectNone() : selectAll()} className={`w-5 h-5 rounded border flex items-center justify-center text-xs mx-auto ${allInViewSelected ? 'bg-green-500 text-white' : 'bg-white border-gray-400'}`}>{allInViewSelected ? '✓' : ''}</button>
                </th>
                {visibleColumns.map(col => {
                  const def = columnDefs[col];
                  if (!def) return null;
                  const isActive = sortColumn === col;
                  return (
                    <th
                      key={col}
                      className={`p-2 border cursor-pointer hover:bg-gray-200 select-none ${def.align === 'right' ? 'text-right' : def.align === 'center' ? 'text-center' : 'text-left'}`}
                      onClick={() => handleSort(col)}
                    >
                      <div className={`flex items-center gap-1 ${def.align === 'right' ? 'justify-end' : def.align === 'center' ? 'justify-center' : ''}`}>
                        <span>{def.label}</span>
                        {def.sortable && <span className={`text-xs ${isActive ? 'text-blue-600' : 'text-gray-400'}`}>{isActive ? (sortDirection === 'asc' ? '▲' : '▼') : '⇅'}</span>}
                      </div>
                    </th>
                  );
                })}
                <th className="p-2 border w-12">⋮</th>
              </tr>
            </thead>
            <tbody>
              {filteredIngredients.map((ing, idx) => {
                const isSelected = selectedItems.has(ing.id);
                const editableFields = ['name', 'brand', 'category', 'subcategory', 'unit', 'vendor', 'vendorCode', 'packSize', 'casePrice'];
                return (
                  <tr key={ing.id} className={`border-t hover:bg-blue-50 ${isSelected ? 'bg-green-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td className="p-2 border text-center">
                      <button onClick={() => toggleSelected(ing.id)} className={`w-5 h-5 rounded border flex items-center justify-center text-xs mx-auto ${isSelected ? 'bg-green-500 text-white' : 'bg-white border-gray-400'}`}>{isSelected ? '✓' : ''}</button>
                    </td>
                    {visibleColumns.map(col => {
                      const def = columnDefs[col];
                      if (!def) return null;
                      if (col === 'instructor') {
                        return (
                          <td key={col} className="p-2 border text-center">
                            <button onClick={() => toggleInstructorVisibility(ing.id)} className={instructorHidden.has(ing.id) ? 'text-red-500' : 'text-green-600'}>{instructorHidden.has(ing.id) ? '✗' : '✓'}</button>
                          </td>
                        );
                      }
                      // Edit mode for editable fields
                      if (editMode && editableFields.includes(col)) {
                        if (col === 'vendor') {
                          return (
                            <td key={col} className="p-1 border">
                              <select
                                defaultValue={ing.vendor || 'Sysco'}
                                onChange={(e) => saveIngredientEdit(ing.id, { vendor: e.target.value })}
                                className="w-full px-1 py-0.5 border rounded text-sm"
                              >
                                {vendors.map(v => <option key={v} value={v}>{v}</option>)}
                              </select>
                            </td>
                          );
                        }
                        if (col === 'category') {
                          return (
                            <td key={col} className="p-1 border">
                              <select
                                defaultValue={ing.category || ''}
                                onChange={(e) => saveIngredientEdit(ing.id, { category: e.target.value })}
                                className="w-full px-1 py-0.5 border rounded text-sm"
                              >
                                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </td>
                          );
                        }
                        if (col === 'subcategory') {
                          return (
                            <td key={col} className="p-1 border">
                              <select
                                defaultValue={ing.subcategory || ''}
                                onChange={(e) => saveIngredientEdit(ing.id, { subcategory: e.target.value })}
                                className="w-full px-1 py-0.5 border rounded text-sm"
                              >
                                <option value="">-</option>
                                {(categoryStructure[ing.category] || []).map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                          );
                        }
                        if (col === 'casePrice') {
                          return (
                            <td key={col} className="p-1 border">
                              <input
                                type="number"
                                step="0.01"
                                defaultValue={ing.casePrice || 0}
                                onBlur={(e) => saveIngredientEdit(ing.id, { casePrice: parseFloat(e.target.value) || 0 })}
                                className="w-20 px-1 py-0.5 border rounded text-sm text-right"
                              />
                            </td>
                          );
                        }
                        // Text input for other editable fields
                        return (
                          <td key={col} className="p-1 border">
                            <input
                              type="text"
                              defaultValue={ing[col] || ''}
                              onBlur={(e) => saveIngredientEdit(ing.id, { [col]: e.target.value })}
                              className={`w-full px-1 py-0.5 border rounded text-sm ${def.align === 'right' ? 'text-right' : ''}`}
                            />
                          </td>
                        );
                      }
                      return (
                        <td key={col} className={`p-2 border ${def.align === 'right' ? 'text-right' : def.align === 'center' ? 'text-center' : ''}`}>
                          {def.render(ing)}
                        </td>
                      );
                    })}
                    <td className="p-2 border text-center relative">
                      <button onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === ing.id ? null : ing.id); }} className="text-gray-500 hover:text-gray-700 text-lg">⋮</button>
                      {openMenuId === ing.id && (
                        <div className="absolute right-0 bottom-full mb-1 bg-white border rounded shadow-lg z-50 min-w-24">
                          <button onClick={() => { handleEdit(ing); setOpenMenuId(null); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100">✎ Edit</button>
                          <button onClick={() => { setVendorModalIngredient(ing); setShowVendorsModal(true); setOpenMenuId(null); }} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100">🏪 Vendors</button>
                          <button onClick={() => handleDuplicateIngredient(ing)} className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100">⧉ Copy</button>
                          <button onClick={() => { handleDeleteIngredient(ing.id); setOpenMenuId(null); }} className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50">🗑 Delete</button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowEditModal(false); setEditingId(null); setEditForm({}); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4">Edit Ingredient</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Name</label>
                <input type="text" value={editForm.name || ''} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select value={editForm.category || ''} onChange={(e) => setEditForm({...editForm, category: e.target.value, subcategory: '', brand: ''})} className="w-full px-3 py-2 border rounded">
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subcategory</label>
                <select value={editForm.subcategory || ''} onChange={(e) => setEditForm({...editForm, subcategory: e.target.value, brand: ''})} className="w-full px-3 py-2 border rounded">
                  <option value="">Select...</option>
                  {(categoryStructure[editForm.category] || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit</label>
                {showCustomUnit ? (
                  <div className="flex gap-1">
                    <input 
                      type="text" 
                      value={customUnitValue} 
                      onChange={(e) => setCustomUnitValue(e.target.value)} 
                      className="flex-1 px-3 py-2 border rounded"
                      placeholder="Enter custom unit..."
                      autoFocus
                    />
                    <button type="button" onClick={() => { setEditForm({...editForm, unit: customUnitValue}); setShowCustomUnit(false); }} className="px-3 py-2 bg-green-500 text-white rounded">✓</button>
                    <button type="button" onClick={() => { setShowCustomUnit(false); setCustomUnitValue(''); }} className="px-3 py-2 bg-gray-300 rounded">✕</button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <select 
                      value={editForm.unit || ''} 
                      onChange={(e) => setEditForm({...editForm, unit: e.target.value})} 
                      className="flex-1 px-3 py-2 border rounded"
                    >
                      <option value="">Select unit...</option>
                      {getUnitsForCategory(editForm.category, editForm.subcategory).map(u => <option key={u} value={u}>{u}</option>)}
                      {editForm.unit && !getUnitsForCategory(editForm.category, editForm.subcategory).includes(editForm.unit) && <option value={editForm.unit}>{editForm.unit} (current)</option>}
                    </select>
                    <button type="button" onClick={() => { setShowCustomUnit(true); setCustomUnitValue(editForm.unit || ''); }} className="px-3 py-2 bg-blue-500 text-white rounded text-sm">+ Add</button>
                  </div>
                )}
                
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Brand</label>
                {showCustomBrand ? (
                  <div className="flex gap-1">
                    <input 
                      type="text" 
                      value={customBrandValue} 
                      onChange={(e) => setCustomBrandValue(e.target.value)} 
                      className="flex-1 px-3 py-2 border rounded"
                      placeholder="Enter new brand..."
                      autoFocus
                    />
                    <button type="button" onClick={async () => { 
                      setEditForm({...editForm, brand: customBrandValue}); 
                      // Also save to settings
                      const { data: settings } = await supabase.from('settings').select('*').eq('key', 'brands').single();
                      const brands = settings?.value || {};
                      const brandKey = (editForm.category || 'Other') + '|' + (editForm.subcategory || 'Other');
                      if (!brands[brandKey]) brands[brandKey] = [];
                      if (!brands[brandKey].includes(customBrandValue)) {
                        brands[brandKey].push(customBrandValue);
                        await supabase.from('settings').upsert({ key: 'brands', value: brands }, { onConflict: 'key' });
                        refreshSettings();
                      }
                      setShowCustomBrand(false); 
                      setCustomBrandValue('');
                    }} className="px-3 py-2 bg-green-500 text-white rounded">✓</button>
                    <button type="button" onClick={() => { setShowCustomBrand(false); setCustomBrandValue(''); }} className="px-3 py-2 bg-gray-300 rounded">✕</button>
                  </div>
                ) : (
                  <div className="flex gap-1">
                    <select 
                      value={editForm.brand || ''} 
                      onChange={(e) => setEditForm({...editForm, brand: e.target.value})} 
                      className="flex-1 px-3 py-2 border rounded"
                    >
                      <option value="">Select brand...</option>
                      {(getBrandsForSubcategory(editForm.category, editForm.subcategory) || []).map(b => <option key={b} value={b}>{b}</option>)}
                      {editForm.brand && !(getBrandsForSubcategory(editForm.category, editForm.subcategory) || []).includes(editForm.brand) && <option value={editForm.brand}>{editForm.brand}</option>}
                    </select>
                    <button type="button" onClick={() => { setShowCustomBrand(true); setCustomBrandValue(''); }} className="px-3 py-2 bg-blue-500 text-white rounded text-sm">+ Add</button>
                  </div>
                )}
                
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor</label>
                <select value={editForm.vendor || 'Sysco'} onChange={(e) => setEditForm({...editForm, vendor: e.target.value, vendorCode: ''})} className="w-full px-3 py-2 border rounded">
                  {vendors.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor Code</label>
                <input type="text" value={editForm.vendorCode || ''} onChange={(e) => setEditForm({...editForm, vendorCode: e.target.value})} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pack Size</label>
                <input type="text" value={editForm.packSize || ''} onChange={(e) => setEditForm({...editForm, packSize: e.target.value})} className="w-full px-3 py-2 border rounded" placeholder="e.g., 6/5LB or 3L" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Case Price</label>
                <input type="number" step="0.01" value={editForm.casePrice || 0} onChange={(e) => setEditForm({...editForm, casePrice: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit Price <span className="text-xs text-gray-400">(calculated)</span></label>
                <div className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700 font-medium">
                  {(() => { const p = calculateUnitPriceFromPack(editForm); return p !== null ? "$" + p.toFixed(4) : "N/A"; })()}
                </div>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Programs</label>
                <div className="flex gap-2">
                  {['Baking & Pastry Arts', 'Culinary Arts', 'Foodservice'].map(p => (
                    <label key={p} className="flex items-center gap-1">
                      <input type="checkbox" checked={(editForm.programs || []).includes(p)} onChange={(e) => {
                        const current = editForm.programs || [];
                        const updated = e.target.checked ? [...current, p] : current.filter(x => x !== p);
                        setEditForm({...editForm, programs: updated});
                      }} />
                      <span className="text-sm">{p === 'Baking & Pastry Arts' ? 'Baking' : p === 'Culinary Arts' ? 'Culinary' : 'Foodservice'}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={handleCancelEdit} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Ingredient Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => { setShowEditModal(false); setEditingId(null); setEditForm({}); }}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h2 className="text-xl font-bold mb-4">Add Ingredient</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Name</label>
                <input type="text" value={newIngredient.name} onChange={(e) => setNewIngredient({...newIngredient, name: e.target.value})} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Category</label>
                <select value={newIngredient.category} onChange={(e) => setNewIngredient({...newIngredient, category: e.target.value})} className="w-full px-3 py-2 border rounded">
                  {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subcategory</label>
                <select value={newIngredient.subcategory} onChange={(e) => setNewIngredient({...newIngredient, subcategory: e.target.value})} className="w-full px-3 py-2 border rounded">
                  {(categoryStructure[newIngredient.category] || ['Other']).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit</label>
                <select value={newIngredient.unit} onChange={(e) => setNewIngredient({...newIngredient, unit: e.target.value})} className="w-full px-3 py-2 border rounded">
                  {units.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Vendor</label>
                <select value={newIngredient.vendor} onChange={(e) => setNewIngredient({...newIngredient, vendor: e.target.value})} className="w-full px-3 py-2 border rounded">
                  {vendors.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Pack Size</label>
                <input type="text" value={newIngredient.packSize} onChange={(e) => setNewIngredient({...newIngredient, packSize: e.target.value})} className="w-full px-3 py-2 border rounded" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Case Price</label>
                <input type="number" step="0.01" value={newIngredient.casePrice} onChange={(e) => setNewIngredient({...newIngredient, casePrice: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400">Cancel</button>
              <button onClick={handleAddIngredient} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Add</button>
            </div>
          </div>
        </div>
      )}
      <BulkUpdateModal isOpen={showBulkUpdate} onClose={() => setShowBulkUpdate(false)} onSave={() => loadIngredients()} />
      {showVendorsModal && vendorModalIngredient && (
        <IngredientVendorsModal
          ingredient={vendorModalIngredient}
          onClose={() => { setShowVendorsModal(false); setVendorModalIngredient(null); }}
          onUpdate={() => loadIngredients()}
        />
      )}
    </div>
  );
}
